import { inject, Injectable, NgZone } from '@angular/core';
import { CommonStore } from '@shared/stores/common.store';
import { UserStore } from '@shared/stores/user.store';
import { BehaviorSubject, fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

export interface InactivityConfig {
  /** Inactivity timeout in milliseconds (default: 15 minutes) */
  timeout: number;
  /** Warning time before timeout in milliseconds (default: 2 minutes) */
  warningTime: number;
  /** DOM events that reset the inactivity timer */
  activityEvents: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UserInactivityService {
  // Default inactivity configuration
  private readonly defaultConfig: InactivityConfig = {
    timeout: 15 * 60 * 1000, // 15 minutes
    warningTime: 2 * 60 * 1000, // 2 minutes
    activityEvents: ['mousedown', 'keypress', 'scroll', 'touchstart', 'click']
  };

  private config: InactivityConfig = { ...this.defaultConfig };
  private isMonitoring = false;
  private activitySubscription?: Subscription;
  private visibilitySub?: Subscription;
  private storageSub?: Subscription;
  private tickId?: any; // setInterval handle

  // Absolute time markers (ms since epoch)
  private deadlineAt = 0; // when session times out
  private warningAt = 0;  // when warning window starts
  private warned = false; // whether warning was emitted

  // Reactive subjects (public API via asObservable)
  private userActiveSubject = new BehaviorSubject<boolean>(true);
  private inactivityWarningSubject = new BehaviorSubject<boolean>(false);
  private timeoutSubject = new BehaviorSubject<boolean>(false);
  private remainingTimeSubject = new BehaviorSubject<number>(0);

  // Public observables for UI or other services to subscribe to
  public readonly userActive$ = this.userActiveSubject.asObservable();
  public readonly inactivityWarning$ = this.inactivityWarningSubject.asObservable();
  public readonly timeout$ = this.timeoutSubject.asObservable();
  public readonly remainingTime$ = this.remainingTimeSubject.asObservable();

  // Dependencies
  public readonly commonStore = inject(CommonStore);
  public readonly userStore = inject(UserStore);

  // Cross-tab sync (localStorage)
  private readonly LS_DEADLINE = 'inactivity.deadlineAt';
  private readonly LS_WARNING  = 'inactivity.warningAt';
  private readonly LS_LAST_ACTION = 'inactivity.lastAction'; // reset|timeout|logout
  private readonly LS_SUPPRESS_UNTIL = 'inactivity.suppressWarningUntil'; // ms epoch
  private readonly instanceId = Math.random().toString(36).slice(2); // identify current tab

  constructor(private ngZone: NgZone) { }

  /**
   * Starts monitoring user inactivity.
   * Can optionally receive a custom configuration.
   */
  startMonitoring(): void {
    // Avoid multiple subscriptions or timers
    if (this.isMonitoring) return;

    const user = this.userStore.user();

    // Monitor only if user is logged in and if is not a NMFTA user
    if (!user?.userId || user.isNmfta) return;

    this.config = this.buildConfigFromVariables();

    this.isMonitoring = true;
    this.resetStates();

    // Create activity stream from DOM events to reset the timer on real user activity
    const activityEvents$ = merge(
      ...this.config.activityEvents.map(event => fromEvent(document, event))
    ).pipe(
      filter((event: Event) => this.shouldCountAsActivity(event)),
      debounceTime(500) // Avoid firing too frequently on rapid events
    );

    // Subscribe to activity events to reset timers
    this.activitySubscription = activityEvents$.subscribe(() => {
      this.onUserActivity();
    });

    // Listen to cross-tab updates (other tabs resetting or timing out)
    this.storageSub = fromEvent<StorageEvent>(window, 'storage')
      .subscribe(ev => this.onStorageEvent(ev));

    // Initialize inactivity timer
    // treat (re)load as a user interaction by reusing onUserActivity()
    this.onUserActivity();
  }

  /**
   * Stops monitoring user inactivity and clears all timers/subscriptions.
   */
  stopMonitoring(): void {
    this.isMonitoring = false;

    this.activitySubscription?.unsubscribe();
    this.activitySubscription = undefined;

    this.stopTick();
    this.visibilitySub?.unsubscribe();
    this.visibilitySub = undefined;

    this.storageSub?.unsubscribe();
    this.storageSub = undefined;

    this.resetStates();
  }

  /**
   * Manually resets the inactivity timer (useful when refreshing tokens or performing actions programmatically).
   */
  resetTimer(): void {
    if (this.isMonitoring) {
      this.onUserActivity();
    }
  }

  /** Returns the current configuration being used. */
  getConfig(): InactivityConfig {
    return { ...this.config };
  }

  /** Returns whether the service is currently monitoring activity. */
  isMonitoringActive(): boolean {
    return this.isMonitoring;
  }

  /** Returns the remaining time (in milliseconds) until timeout. */
  getRemainingTime(): number {
    return this.remainingTimeSubject.value;
  }

  /**
   * Build a safe, clamped config from CommonStore variables.
   * Ensures timeout > warningTime and applies sensible minimums.
   */
  private buildConfigFromVariables(): InactivityConfig {
    const minutes = this.commonStore.variables().idleTimeInMinutes ?? 120;

    // Convert to ms and clamp to safe minimums
    const timeoutMs = Math.max(30_000, minutes * 60_000); // >= 30s total
    // Default warning: 2 minutes, but never >= timeout; at least 15s
    const proposedWarning = 2 * 60_000;
    const warningMs = Math.max(15_000, Math.min(proposedWarning, timeoutMs - 1_000));

    return {
      ...this.defaultConfig,
      timeout: timeoutMs,
      warningTime: warningMs,
    };
  }

  /** Called on any detected user activity to reset timers. */
  private onUserActivity(): void {
    if (!this.isMonitoring) return;

    // Emit that the user is active again
    this.userActiveSubject.next(true);
    this.inactivityWarningSubject.next(false);
    this.timeoutSubject.next(false);

    // Instead of adopting from storage here, we recompute fresh deadlines from "now"
    // Clear and restart timers locally
    this.stopTick();

    const now = Date.now();
    this.deadlineAt = now + this.config.timeout;
    this.warningAt  = this.deadlineAt - this.config.warningTime;
    this.warned = false;

    // Persist first a suppression window, then the new schedule, then the action
    this.persistScheduleToStorage('reset');

    // Start the ticking loop again
    this.startTick();

    // also ensure visibility listener is active after a manual reset
    if (!this.visibilitySub) {
      this.visibilitySub = fromEvent(document, 'visibilitychange').subscribe(() => {
        this.evaluateSchedule();
      });
    }
  }

  /** Start the 1 second evaluation loop (and run once immediately). */
  private startTick(): void {
    if (this.tickId) clearInterval(this.tickId);
    this.tickId = setInterval(() => this.evaluateSchedule(), 1_000);
    this.evaluateSchedule();
  }

  /** Stop the evaluation loop and visibility listener. */
  private stopTick(): void {
    if (this.tickId) {
      clearInterval(this.tickId);
      this.tickId = undefined;
    }
  }

  /**
   * Evaluate current time against warning/timeout deadlines and emit:
   * - remaining time (ms)
   * - warning start (once)
   * - timeout (once)
   */
  private evaluateSchedule(): void {
    const now = Date.now();

    // Emit remaining time continuously (>= 0)
    const remaining = Math.max(0, this.deadlineAt - now);
    this.ngZone.run(() => this.remainingTimeSubject.next(remaining));

    // Respect a cross-tab "suppress warning" window right after user action
    const suppressUntil = Number(localStorage.getItem(this.LS_SUPPRESS_UNTIL) || 0);
    const canWarn = now >= suppressUntil;

    // Enter warning window (only once)
    if (canWarn && !this.warned && now >= this.warningAt && now < this.deadlineAt) {
      this.warned = true;
      this.ngZone.run(() => this.inactivityWarningSubject.next(true));
    }

    // Timeout reached
    if (now >= this.deadlineAt) {
      this.ngZone.run(() => {
        this.userActiveSubject.next(false);
        this.inactivityWarningSubject.next(false);
        this.timeoutSubject.next(true);
        this.remainingTimeSubject.next(0);
      });
      this.stopTick(); // stop evaluating after timeout

      // Broadcast timeout to other tabs
      this.persistScheduleToStorage('timeout');
    }
  }

  /** Resets all state subjects to default values. */
  private resetStates(): void {
    this.userActiveSubject.next(true);
    this.inactivityWarningSubject.next(false);
    this.timeoutSubject.next(false);
    this.remainingTimeSubject.next(0);
  }

  /**
   * Determines whether a DOM event should be considered valid user activity.
   * Ignores events triggered inside the inactivity dialog.
   */
  private shouldCountAsActivity(event: Event): boolean {
    const target = event.target as Element | null;

    // Allow if no target element exists
    if (!target) return true;

    // Ignore events occurring inside the inactivity warning dialog
    const inactivityDialog = target.closest('.inactivity-dialog');
    return !inactivityDialog;
  }

  /**
   * Persist the current schedule (warningAt/deadlineAt) to localStorage so that
   * other tabs can synchronize their timers.
   *
   * We now set a slightly larger suppression window (5s) *before* writing
   * the new schedule, to make sure other tabs suppress any late warning emissions
   * while adopting the new deadlines.
   */
  private persistScheduleToStorage(action: 'reset' | 'timeout' | 'logout'): void {
    try {
      // Write a suppression window first to absorb late emissions across tabs
      if (action === 'reset') {
        const suppressMs = 5_000; // 5s cooldown across tabs
        localStorage.setItem(this.LS_SUPPRESS_UNTIL, String(Date.now() + suppressMs));
      }

      // Publish the updated schedule
      localStorage.setItem(this.LS_DEADLINE, String(this.deadlineAt));
      localStorage.setItem(this.LS_WARNING, String(this.warningAt));

      // And finally, the action marker
      localStorage.setItem(this.LS_LAST_ACTION, JSON.stringify({
        at: Date.now(),
        action,
        by: this.instanceId
      }));
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }

  /**
   * Attempt to adopt an existing schedule from localStorage if it exists
   * and is still in the future. Returns true if adoption happened.
   */
  private tryAdoptScheduleFromStorage(now: number): boolean {
    try {
      const lsDeadline = Number(localStorage.getItem(this.LS_DEADLINE) || 0);
      const lsWarning  = Number(localStorage.getItem(this.LS_WARNING)  || 0);

      if (lsDeadline > now && lsWarning > 0 && lsWarning < lsDeadline) {
        this.deadlineAt = lsDeadline;
        this.warningAt  = lsWarning;
        this.warned = false; // ensure warning can show later when we truly enter the window
        return true;
      }
    } catch {
      // ignore parse/storage errors
    }
    return false;
  }

  /**
   * React to changes coming from other tabs:
   * - "reset": recalculate local state to the shared schedule and close warning if open
   * - "timeout": stop ticking locally and emit timeout if not already done
   * - "logout": (reserved if you decide to broadcast explicit logout)
   *
   * On "reset", we now adopt the new schedule, clear any warning state,
   * and ensure the local tick is running so the remaining time visibly restarts.
   */
  private onStorageEvent(ev: StorageEvent): void {
    if (!ev) return;

    // Only react to our keys; ignore this tab's own writes (same-tab 'storage' doesn't fire anyway)
    if (
      ev.key !== this.LS_LAST_ACTION &&
      ev.key !== this.LS_DEADLINE &&
      ev.key !== this.LS_WARNING &&
      ev.key !== this.LS_SUPPRESS_UNTIL
    ) {
      return;
    }

    const now = Date.now();

    // Always try to adopt the latest schedule if deadline is still in the future
    const adopted = this.tryAdoptScheduleFromStorage(now);

    // Inspect last action for coordination hints
    try {
      const payload = localStorage.getItem(this.LS_LAST_ACTION);
      if (payload) {
        const { action } = JSON.parse(payload) as { at: number; action: 'reset' | 'timeout' | 'logout'; by: string };

        if (action === 'reset') {
          // Close local warning and restart ticking with the adopted schedule
          this.warned = false;
          this.ngZone.run(() => {
            this.inactivityWarningSubject.next(false);
            this.timeoutSubject.next(false);
            this.userActiveSubject.next(true);
          });

          // If we adopted a new schedule, restart tick to make the restart visible immediately
          if (adopted) {
            this.stopTick();
            this.startTick();
          }
        }

        if (action === 'timeout') {
          // Another tab reached timeout; align state locally
          if (now >= this.deadlineAt) {
            this.ngZone.run(() => {
              this.userActiveSubject.next(false);
              this.inactivityWarningSubject.next(false);
              this.timeoutSubject.next(true);
              this.remainingTimeSubject.next(0);
            });
            this.stopTick();
          }
        }
      }
    } catch {
      // ignore malformed payloads
    }
  }
}
